import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/db';
import { useAuth } from '../lib/auth';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface UserList {
  id: string;
  name: string;
  description: string;
  member_count: number;
  created_at: string;
  is_active: boolean;
}

interface User {
  id: string;
  email: string;
  user_metadata: any;
  last_sign_in_at: string | null;
  created_at: string;
}

interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  message_template: string;
  html_template?: string;
  sender_name: string;
  sender_email: string;
  banner_image_url?: string;
  status: 'draft' | 'sending' | 'sent' | 'cancelled';
  total_recipients: number;
  emails_sent: number;
  emails_delivered: number;
  emails_opened: number;
  delivery_rate: number;
  open_rate: number;
  sent_at: string | null;
  created_at: string;
}

interface UndeliveredAddress {
  email: string;
  status: string;
  error_message?: string;
  sent_at?: string;
  bounced_at?: string;
  failed_at?: string;
}

interface OpenedBy {
  email: string;
  user_id: string;
  opened_at: string;
  user_name: string;
}

interface CampaignAnalytics {
  campaign_id: string;
  campaign_name: string;
  status: string;
  total_recipients: number;
  emails_sent: number;
  emails_delivered: number;
  emails_opened: number;
  emails_clicked: number;
  emails_bounced: number;
  emails_failed: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
  undelivered_addresses?: UndeliveredAddress[];
  opened_by?: OpenedBy[];
  delivered_addresses?: Array<{ email: string; delivered_at: string }>;
}

interface CampaignRecipient {
  id: string;
  email_address: string;
  user_id: string;
  status: string;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  failed_at: string | null;
  error_message: string | null;
  user_name: string | null;
  sendgrid_message_id: string | null;
}

export default function AdminBulkEmail() {
  const { user: _user } = useAuth();
  const [activeTab, setActiveTab] = useState<'campaigns' | 'lists' | 'create-campaign' | 'create-list' | 'manage-list'>('campaigns');
  
  // State for campaigns
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState<CampaignAnalytics | null>(null);
  const [viewingCampaign, setViewingCampaign] = useState<EmailCampaign | null>(null);
  
  // State for recipient log
  const [viewingRecipients, setViewingRecipients] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [recipientStatusFilter, setRecipientStatusFilter] = useState<string>('all');
  const [recipientSearchTerm, setRecipientSearchTerm] = useState<string>('');
  
  // State for user lists
  const [userLists, setUserLists] = useState<UserList[]>([]);
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  
  // State for creating campaigns
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    subject: '',
    message_template: '',
    html_template: '',
    sender_name: 'Wayja',
    sender_email: 'noreply@wayja.me',
    banner_image_url: ''
  });
  
  const [uploadingBanner, setUploadingBanner] = useState(false);
  
  const handleBannerUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image size must be less than 2MB');
      return;
    }

    setUploadingBanner(true);
    setError(null);

    try {
      // Upload to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `email-banners/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('email-banners')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('email-banners')
        .getPublicUrl(filePath);

      setNewCampaign({ ...newCampaign, banner_image_url: publicUrl });
      setSuccess('Banner uploaded successfully!');
    } catch (err: any) {
      setError(`Failed to upload banner: ${err.message}`);
    } finally {
      setUploadingBanner(false);
    }
  };

  const removeBanner = () => {
    setNewCampaign({ ...newCampaign, banner_image_url: '' });
  };
  
  // State for creating lists
  const [newList, setNewList] = useState({
    name: '',
    description: ''
  });
  
  // State for managing lists
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [allMemberIds, setAllMemberIds] = useState<string[]>([]); // Track ALL member IDs for filtering
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Filter states
  const [loginFilter, setLoginFilter] = useState<'all' | 'never' | 'recent' | 'inactive' | 'custom'>('all');
  const [accountAgeFilter, setAccountAgeFilter] = useState<'all' | 'new' | 'old' | 'custom'>('all');
  const [customDays, setCustomDays] = useState<number>(30);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Sending progress tracking
  const [sendingProgress, setSendingProgress] = useState<{
    campaignId: string;
    campaignName: string;
    batchNumber: number;
    totalBatches: number;
    sent: number;
    failed: number;
    status: 'preparing' | 'sending' | 'completed' | 'error';
    message: string;
    startedAt: Date;
  } | null>(null);
  
  // Auto-refresh for sending campaigns
  const [isPolling, setIsPolling] = useState(false);

  // Apply filters to user list
  const applyFilters = (users: User[]): User[] => {
    return users.filter(user => {
      const now = new Date();
      const createdAt = new Date(user.created_at);
      const lastSignInAt = user.last_sign_in_at ? new Date(user.last_sign_in_at) : null;

      // Account age filter
      if (accountAgeFilter === 'new') {
        // Created in last 7 days
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        if (createdAt < sevenDaysAgo) return false;
      } else if (accountAgeFilter === 'old') {
        // Created more than 30 days ago
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        if (createdAt > thirtyDaysAgo) return false;
      } else if (accountAgeFilter === 'custom') {
        // Created more than customDays ago
        const customDaysAgo = new Date(now.getTime() - customDays * 24 * 60 * 60 * 1000);
        if (createdAt > customDaysAgo) return false;
      }

      // Login filter
      if (loginFilter === 'never') {
        // Never logged in
        if (lastSignInAt !== null) return false;
      } else if (loginFilter === 'recent') {
        // Logged in within last 7 days
        if (!lastSignInAt) return false;
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        if (lastSignInAt < sevenDaysAgo) return false;
      } else if (loginFilter === 'inactive') {
        // Haven't logged in for 30+ days
        if (!lastSignInAt) return true; // Include never logged in users
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        if (lastSignInAt > thirtyDaysAgo) return false;
      } else if (loginFilter === 'custom') {
        // Haven't logged in for customDays or more
        if (!lastSignInAt) return true; // Include never logged in users
        const customDaysAgo = new Date(now.getTime() - customDays * 24 * 60 * 60 * 1000);
        if (lastSignInAt > customDaysAgo) return false;
      }

      return true;
    });
  };

  // Load data on component mount
  useEffect(() => {
    loadCampaigns();
    loadUserLists();
    testAdminAccess(); // Test admin access on load
  }, []);

  // Check if any campaigns are sending (memoized to prevent infinite loops)
  const hasSendingCampaigns = campaigns.some(c => c.status === 'sending');
  
  // Auto-refresh when there are sending campaigns
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    
    if (hasSendingCampaigns) {
      setIsPolling(true);
      interval = setInterval(() => {
        loadCampaigns();
      }, 5000); // Refresh every 5 seconds
    } else {
      setIsPolling(false);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [hasSendingCampaigns]); // Only re-run when sending status changes

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Auto-dismiss error messages after 8 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const testAdminAccess = async () => {
    try {
      console.log('Testing admin access...');
      const { data, error } = await supabase.rpc('test_admin_access');
      if (error) {
        console.error('Admin access test error:', error);
      } else {
        console.log('Admin access test result:', data);
      }
    } catch (err) {
      console.error('Failed to test admin access:', err);
    }
  };

  const loadCampaigns = async () => {
    try {
      const { data, error } = await supabase.rpc('get_email_campaigns');
      if (error) throw error;
      setCampaigns(data || []);
    } catch (err: any) {
      setError(`Failed to load campaigns: ${err.message}`);
    }
  };

  const loadUserLists = async () => {
    try {
      const { data, error } = await supabase.rpc('get_user_lists');
      if (error) throw error;
      setUserLists(data || []);
    } catch (err: any) {
      setError(`Failed to load user lists: ${err.message}`);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      console.log('Loading all users with auth data...');
      let allUsers: User[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        // Call RPC function to get users with auth data
        const { data, error } = await supabase.rpc('get_bulk_email_users', {
          p_limit: pageSize,
          p_offset: from
        });
        
        if (error) {
          console.error('Error loading users:', error);
          throw error;
        }
        
        if (data && data.length > 0) {
          // Transform the data to match our User interface
          const transformedData = data.map((user: any) => ({
            id: user.id,
            email: user.email,
            user_metadata: user.user_metadata,
            created_at: user.created_at,
            last_sign_in_at: user.last_sign_in_at
          }));

          allUsers = [...allUsers, ...transformedData];
          console.log(`Loaded batch: ${data.length} users (total so far: ${allUsers.length})`);
          
          // Check if we have more data
          hasMore = data.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }
        
        // Safety break to prevent infinite loops
        if (from > 100000) {
          console.warn('Stopping user load at 100,000 users to prevent infinite loop');
          break;
        }
      }
      
      setAvailableUsers(allUsers);
      console.log(`Final: Loaded ${allUsers.length} total users for search`);
      
      // Log some sample emails to check the range
      if (allUsers.length > 0) {
        console.log('First user email:', allUsers[0].email);
        console.log('Last user email:', allUsers[allUsers.length - 1].email);
        console.log('Sample emails:', allUsers.slice(0, 5).map(u => u.email));
        
        // Check for users starting with different letters
        const emailsByLetter: { [key: string]: number } = {};
        allUsers.forEach(user => {
          const firstLetter = user.email.charAt(0).toUpperCase();
          emailsByLetter[firstLetter] = (emailsByLetter[firstLetter] || 0) + 1;
        });
        console.log('Users by first letter:', emailsByLetter);
      }
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError(`Failed to load users: ${err.message}`);
    }
  };

  const loadListMembers = async (listId: string) => {
    try {
      console.log('Loading list members for list:', listId);
      
      // First, get ALL member IDs for filtering (use pagination to ensure we get all)
      let allIds: string[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data: memberData, error: memberError } = await supabase
        .from('user_list_members')
        .select('user_id')
          .eq('list_id', listId)
          .range(from, from + pageSize - 1);

        if (memberError) {
          console.error('Error loading member IDs batch:', memberError);
          throw memberError;
        }

        if (memberData && memberData.length > 0) {
          const batchIds = memberData.map(m => m.user_id);
          allIds = [...allIds, ...batchIds];
          hasMore = memberData.length === pageSize;
          from += pageSize;
        } else {
          hasMore = false;
        }

        // Safety limit
        if (from > 100000) {
          console.warn('Reached safety limit loading member IDs');
          break;
        }
      }

      setAllMemberIds(allIds); // Store all member IDs for filtering
      console.log(`Total members in list: ${allIds.length}`);
      
      // Note: In the new unified interface, we don't need to load member details
      // since all user information is shown in the main table
      console.log(`Loaded member IDs for filtering (total: ${allIds.length})`);
    } catch (err: any) {
      console.error('Full loadListMembers error:', err);
      setError(`Failed to load list members: ${err.message}`);
    }
  };

  const loadCampaignAnalytics = async (campaignId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_campaign_analytics', {
        p_campaign_id: campaignId
      });
      if (error) throw error;
      setCampaignAnalytics(data);
    } catch (err: any) {
      setError(`Failed to load analytics: ${err.message}`);
    }
  };

  const loadCampaignRecipients = async (campaignId: string) => {
    try {
      setLoading(true);
      
      // Fetch all recipients in batches to bypass PostgREST 1000 row limit
      const allRecipients: any[] = [];
      const batchSize = 1000;
      let offset = 0;
      let hasMore = true;
      
      while (hasMore) {
        const { data, error } = await supabase
          .rpc('get_campaign_recipients', {
            p_campaign_id: campaignId,
            p_status_filter: null
          })
          .range(offset, offset + batchSize - 1);
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          allRecipients.push(...data);
          offset += batchSize;
          hasMore = data.length === batchSize;
        } else {
          hasMore = false;
        }
      }
      
      // Deduplicate by id (in case ranges overlap)
      const uniqueRecipients = Array.from(
        new Map(allRecipients.map(r => [r.id, r])).values()
      );
      console.log('[Recipients] Loaded total:', allRecipients.length, 'Unique:', uniqueRecipients.length);
      setRecipients(uniqueRecipients);
      setViewingRecipients(campaignId);
    } catch (err: any) {
      setError(`Failed to load recipients: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    if (!newCampaign.name || !newCampaign.subject || !newCampaign.message_template) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: campaignId, error } = await supabase.rpc('create_email_campaign', {
        p_name: newCampaign.name,
        p_subject: newCampaign.subject,
        p_message_template: newCampaign.message_template,
        p_html_template: newCampaign.html_template || null,
        p_sender_name: newCampaign.sender_name,
        p_sender_email: newCampaign.sender_email,
        p_banner_image_url: newCampaign.banner_image_url || null
      });

      if (error) throw error;

      // Assign selected lists to the campaign
      if (selectedLists.length > 0) {
        const { error: assignError } = await supabase.rpc('assign_lists_to_campaign', {
          p_campaign_id: campaignId,
          p_list_ids: selectedLists
        });
        if (assignError) throw assignError;
      }

      // Prepare recipients
      const { error: prepareError } = await supabase.rpc('prepare_campaign_recipients', {
        p_campaign_id: campaignId
      });
      if (prepareError) throw prepareError;

      setSuccess('Campaign created successfully!');
      setNewCampaign({
        name: '',
        subject: '',
        message_template: '',
        html_template: '',
        sender_name: 'Wayja',
        sender_email: 'noreply@wayja.me',
        banner_image_url: ''
      });
      setSelectedLists([]);
      loadCampaigns();
      setActiveTab('campaigns');
    } catch (err: any) {
      setError(`Failed to create campaign: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createUserList = async () => {
    if (!newList.name) {
      setError('Please enter a list name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.rpc('create_user_list', {
        p_name: newList.name,
        p_description: newList.description || null
      });

      if (error) throw error;

      setSuccess('User list created successfully!');
      setNewList({ name: '', description: '' });
      loadUserLists();
      setActiveTab('lists');
    } catch (err: any) {
      setError(`Failed to create user list: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const sendCampaign = async (campaignId: string) => {
    // Find campaign name for progress display
    const campaign = campaigns.find(c => c.id === campaignId);
    const campaignName = campaign?.name || 'Campaign';
    
    setLoading(true);
    setError(null);
    
    // Set initial progress state
    setSendingProgress({
      campaignId,
      campaignName,
      batchNumber: 0,
      totalBatches: 0,
      sent: 0,
      failed: 0,
      status: 'preparing',
      message: 'Preparing campaign...',
      startedAt: new Date()
    });

    try {
      const { data, error } = await supabase.functions.invoke('bulk-email-sender', {
        body: {
          campaign_id: campaignId,
          batch_size: 25,
          delay_between_batches: 2000
        }
      });

      if (error) throw error;

      // Update progress with results
      setSendingProgress(prev => prev ? {
        ...prev,
        batchNumber: data.batch_number || 1,
        totalBatches: data.total_batches || 1,
        sent: data.sent || 0,
        failed: data.failed || 0,
        status: 'sending',
        message: data.message || 'Campaign started! Processing batches...'
      } : null);

      setSuccess(`🚀 Campaign started! ${data.sent} emails sent in first batch. Remaining batches processing automatically.`);
      loadCampaigns();
      
      // Keep progress visible for a while then clear
      setTimeout(() => {
        setSendingProgress(null);
      }, 10000);
    } catch (err: any) {
      setSendingProgress(prev => prev ? {
        ...prev,
        status: 'error',
        message: `Error: ${err.message}`
      } : null);
      setError(`Failed to send campaign: ${err.message}`);
      
      setTimeout(() => {
        setSendingProgress(null);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const resumeCampaign = async (campaignId: string) => {
    setLoading(true);
    setError(null);

    try {
      // Resume by triggering the next batch
      // Pass campaign_id so the function can initialize batches if needed
      const { data, error } = await supabase.functions.invoke('bulk-email-sender', {
        body: {
          campaign_id: campaignId,
          process_next_batch: true,
          batch_size: 25
        }
      });

      if (error) throw error;

      if (data?.message?.includes('No pending')) {
        setError(data.message || 'No pending batches or recipients to process');
      } else {
        setSuccess(`Campaign resumed! ${data?.message || 'Processing next batch...'}`);
      }
      
      loadCampaigns();
      
      // Refresh after a short delay to see updated status
      setTimeout(() => {
        loadCampaigns();
      }, 3000);
    } catch (err: any) {
      setError(`Failed to resume campaign: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteCampaign = async (campaignId: string, campaignName: string) => {
    if (!confirm(`Are you sure you want to delete the campaign "${campaignName}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await supabase
        .from('email_campaigns')
        .delete()
        .eq('id', campaignId);

      if (error) throw error;

      setSuccess(`Campaign "${campaignName}" deleted successfully.`);
      loadCampaigns(); // Refresh the campaigns list
    } catch (err: any) {
      setError(`Failed to delete campaign: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const viewCampaign = async (campaignId: string) => {
    try {
      const { data, error } = await supabase
        .from('email_campaigns')
        .select('*')
        .eq('id', campaignId)
        .single();

      if (error) throw error;
      setViewingCampaign(data);
    } catch (err: any) {
      setError(`Failed to load campaign details: ${err.message}`);
    }
  };

  const deleteUserList = async (listId: string, listName: string) => {
    if (!confirm(`Are you sure you want to delete the user list "${listName}"? This will also remove all members from the list. This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // First delete all members from the list
      const { error: membersError } = await supabase
        .from('user_list_members')
        .delete()
        .eq('list_id', listId);

      if (membersError) throw membersError;

      // Then delete any campaign associations
      const { error: campaignError } = await supabase
        .from('campaign_user_lists')
        .delete()
        .eq('user_list_id', listId);

      if (campaignError) throw campaignError;

      // Finally delete the list itself
      const { error: listError } = await supabase
        .from('user_lists')
        .delete()
        .eq('id', listId);

      if (listError) throw listError;

      setSuccess(`User list "${listName}" deleted successfully.`);
      loadUserLists(); // Refresh the lists
    } catch (err: any) {
      setError(`Failed to delete user list: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };


  const addUsersToList = async (listId: string, userIds: string[]) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Adding users to list:', { listId, userIds: userIds.length });
      
      // Process in batches to avoid timeout/size limits
      const batchSize = 100; // Process 100 users at a time
      let totalAdded = 0;
      
      for (let i = 0; i < userIds.length; i += batchSize) {
        const batch = userIds.slice(i, i + batchSize);
        console.log(`Processing batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(userIds.length / batchSize)} (${batch.length} users)`);
        console.log(`Batch user IDs:`, batch.slice(0, 3), '...', batch.slice(-2)); // Show first 3 and last 2 IDs
        
        const { data, error } = await supabase.rpc('add_users_to_list', {
          p_list_id: listId,
          p_user_ids: batch
        });

        console.log(`RPC response for batch ${Math.floor(i / batchSize) + 1}:`, { data, error });

        if (error) {
          console.error('RPC error details for batch:', error);
          throw error;
        }

        const batchAdded = data || 0;
        totalAdded += batchAdded;
        console.log(`Batch ${Math.floor(i / batchSize) + 1} completed. Added ${batchAdded} users. Total so far: ${totalAdded}`);
        
        // If a batch adds 0 users, log this as it might indicate all users in that batch are already members
        if (batchAdded === 0) {
          console.log(`⚠️ Batch ${Math.floor(i / batchSize) + 1} added 0 users - all users in this batch may already be members`);
        }
        
        // Small delay between batches to avoid overwhelming the database
        if (i + batchSize < userIds.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      console.log(`All batches completed. Total added: ${totalAdded}`);
      setSuccess(`Successfully added ${totalAdded} users to the list!`);
      setSelectedUsers([]);
      loadListMembers(listId);
      loadUserLists(); // Refresh member counts
    } catch (err: any) {
      console.error('Full error object:', err);
      setError(`Failed to add users to list: ${err.message || err.details || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const removeUsersFromList = async (listId: string, userIds: string[]) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc('remove_users_from_list', {
        p_list_id: listId,
        p_user_ids: userIds
      });

      if (error) throw error;

      setSuccess(`Successfully removed ${data} users from the list!`);
      loadListMembers(listId);
      loadUserLists(); // Refresh member counts
    } catch (err: any) {
      setError(`Failed to remove users from list: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Status badge component with icons
  const StatusBadge = ({ status }: { status: string }) => {
    const configs: Record<string, { bg: string; text: string; icon: string; pulse?: boolean }> = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-700', icon: '📝' },
      sending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '⏳', pulse: true },
      sent: { bg: 'bg-green-100', text: 'text-green-800', icon: '✅' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: '❌' },
      pending: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '🔄' },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', icon: '📬' },
      opened: { bg: 'bg-purple-100', text: 'text-purple-800', icon: '👁️' },
      clicked: { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: '🖱️' },
      bounced: { bg: 'bg-orange-100', text: 'text-orange-800', icon: '↩️' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', icon: '❌' },
      queued: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '📋' },
    };
    
    const config = configs[status] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: '❓' };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text} ${config.pulse ? 'animate-pulse' : ''}`}>
        <span>{config.icon}</span>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Use StatusBadge for recipient status display
  const getRecipientStatusBadge = (status: string) => {
    return <StatusBadge status={status} />;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
            <h1 className="text-2xl font-bold text-gray-900">Bulk Email Management</h1>
            <p className="text-gray-600 mt-1">Manage email campaigns and user lists</p>
              </div>
              <div className="flex items-center gap-4">
                {isPolling && (
                  <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full">
                    <span className="animate-pulse">●</span>
                    <span>Auto-refreshing...</span>
                  </div>
                )}
                {loading && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Loading...</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'campaigns', label: 'Campaigns' },
                { id: 'lists', label: 'User Lists' },
                { id: 'create-campaign', label: 'Create Campaign' },
                { id: 'create-list', label: 'Create List' },
                { id: 'manage-list', label: 'Manage Lists' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Sending Progress Card */}
            {sendingProgress && (
              <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      sendingProgress.status === 'preparing' ? 'bg-blue-100 text-blue-600' :
                      sendingProgress.status === 'sending' ? 'bg-yellow-100 text-yellow-600' :
                      sendingProgress.status === 'completed' ? 'bg-green-100 text-green-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {sendingProgress.status === 'preparing' && '⏳'}
                      {sendingProgress.status === 'sending' && '📤'}
                      {sendingProgress.status === 'completed' && '✅'}
                      {sendingProgress.status === 'error' && '❌'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{sendingProgress.campaignName}</h3>
                      <p className="text-sm text-gray-600">{sendingProgress.message}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setSendingProgress(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                
                {sendingProgress.totalBatches > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Batch {sendingProgress.batchNumber} of {sendingProgress.totalBatches}</span>
                      <span>{sendingProgress.sent} sent • {sendingProgress.failed} failed</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                        style={{ width: `${(sendingProgress.batchNumber / sendingProgress.totalBatches) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {sendingProgress.status === 'sending' && (
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <span className="animate-pulse">●</span>
                    Processing remaining batches automatically in the background...
                  </p>
                )}
              </div>
            )}

            {/* Error/Success Notifications */}
            {error && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r flex items-start gap-3 shadow-sm">
                <span className="text-xl">⚠️</span>
                <div className="flex-1">
                  <p className="font-medium">Something went wrong</p>
                  <p className="text-sm">{error}</p>
                </div>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">✕</button>
              </div>
            )}
            {success && (
              <div className="mb-4 bg-green-50 border-l-4 border-green-500 text-green-700 px-4 py-3 rounded-r flex items-start gap-3 shadow-sm">
                <span className="text-xl">✅</span>
                <div className="flex-1">
                  <p className="font-medium">Success!</p>
                  <p className="text-sm">{success}</p>
                </div>
                <button onClick={() => setSuccess(null)} className="text-green-400 hover:text-green-600">✕</button>
              </div>
            )}

            {/* Campaigns Tab */}
            {activeTab === 'campaigns' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold">Email Campaigns</h2>
                  <button
                    onClick={loadCampaigns}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  >
                    Refresh
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Campaign
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Recipients
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Performance
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sent At
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {campaigns.map((campaign) => (
                        <tr key={campaign.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                              <div className="text-sm text-gray-500">{campaign.subject}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <StatusBadge status={campaign.status} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{campaign.total_recipients}</span>
                              <span className="text-xs text-gray-500">recipients</span>
                            </div>
                            {campaign.status === 'sending' && campaign.emails_sent > 0 && (
                              <div className="mt-1">
                                <div className="text-xs text-gray-500 mb-1">
                                  {campaign.emails_sent} / {campaign.total_recipients} sent
                                </div>
                                <div className="w-24 bg-gray-200 rounded-full h-1.5">
                                  <div 
                                    className="bg-yellow-500 h-1.5 rounded-full transition-all"
                                    style={{ width: `${(campaign.emails_sent / campaign.total_recipients) * 100}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {campaign.status === 'sent' || campaign.status === 'sending' ? (
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                  <div className="w-16 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                    <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${Math.min(campaign.delivery_rate, 100)}%` }} />
                              </div>
                                  <span className="text-xs text-gray-600">{campaign.delivery_rate.toFixed(1)}% delivered</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-16 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                    <div className="bg-purple-500 h-1.5 rounded-full" style={{ width: `${Math.min(campaign.open_rate, 100)}%` }} />
                                  </div>
                                  <span className="text-xs text-gray-600">{campaign.open_rate.toFixed(1)}% opened</span>
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {campaign.sent_at ? formatDate(campaign.sent_at) : 'Not sent'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => viewCampaign(campaign.id)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                View
                              </button>
                              <button
                                onClick={() => loadCampaignRecipients(campaign.id)}
                                className="text-purple-600 hover:text-purple-900"
                                title="View recipient log"
                              >
                                Recipients
                              </button>
                              {campaign.status === 'draft' && (
                                <button
                                  onClick={() => sendCampaign(campaign.id)}
                                  disabled={loading}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  Send Now
                                </button>
                              )}
                              {campaign.status === 'sending' && (
                                <button
                                  onClick={() => resumeCampaign(campaign.id)}
                                  disabled={loading}
                                  className="text-orange-600 hover:text-orange-900"
                                  title="Resume processing if stuck"
                                >
                                  Resume
                                </button>
                              )}
                              {campaign.status === 'sent' && (
                                <>
                                <button
                                  onClick={() => {
                                    setSelectedCampaign(campaign.id);
                                    loadCampaignAnalytics(campaign.id);
                                  }}
                                  className="text-green-600 hover:text-green-900"
                                >
                                  Analytics
                                </button>
                                  <button
                                    onClick={() => resumeCampaign(campaign.id)}
                                    disabled={loading}
                                    className="text-orange-600 hover:text-orange-900"
                                    title="Resume sending if there are pending emails"
                                  >
                                    Retry
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => deleteCampaign(campaign.id, campaign.name)}
                                disabled={loading}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Campaign View Modal */}
                {viewingCampaign && (
                  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Campaign Details</h3>
                        <button
                          onClick={() => setViewingCampaign(null)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          ✕
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Campaign Name</label>
                          <p className="mt-1 text-sm text-gray-900">{viewingCampaign.name}</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Subject Line</label>
                          <p className="mt-1 text-sm text-gray-900">{viewingCampaign.subject}</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Status</label>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            viewingCampaign.status === 'sent' ? 'bg-green-100 text-green-800' :
                            viewingCampaign.status === 'sending' ? 'bg-yellow-100 text-yellow-800' :
                            viewingCampaign.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {viewingCampaign.status.charAt(0).toUpperCase() + viewingCampaign.status.slice(1)}
                          </span>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Message Template</label>
                          <div className="mt-1 p-3 bg-gray-50 rounded-md">
                            <div 
                              className="text-sm text-gray-900"
                              dangerouslySetInnerHTML={{
                                __html: viewingCampaign.message_template
                                  .replace(/\{\{first_name\}\}/g, '<span class="bg-yellow-100 px-1 rounded">John</span>')
                                  .replace(/\{\{last_name\}\}/g, '<span class="bg-yellow-100 px-1 rounded">Doe</span>')
                                  .replace(/\{\{email\}\}/g, '<span class="bg-yellow-100 px-1 rounded">john.doe@example.com</span>')
                                  .replace(/\{\{full_name\}\}/g, '<span class="bg-yellow-100 px-1 rounded">John Doe</span>')
                              }}
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Email Preview</label>
                          <div className="mt-1 p-4 bg-white border rounded-md">
                            <div className="border-b pb-2 mb-4">
                              <p className="text-sm text-gray-600">From: {viewingCampaign.sender_name} &lt;{viewingCampaign.sender_email}&gt;</p>
                              <p className="text-sm text-gray-600">Subject: {viewingCampaign.subject}</p>
                            </div>
                            <div 
                              className="prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{
                                __html: viewingCampaign.message_template
                                  .replace(/\{\{first_name\}\}/g, 'John')
                                  .replace(/\{\{last_name\}\}/g, 'Doe')
                                  .replace(/\{\{email\}\}/g, 'john.doe@example.com')
                                  .replace(/\{\{full_name\}\}/g, 'John Doe')
                                  .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #3182ce; text-decoration: underline;">$1</a>')
                              }}
                            />
                          </div>
                        </div>
                        
                        {viewingCampaign.total_recipients && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Recipients</label>
                            <p className="mt-1 text-sm text-gray-900">{viewingCampaign.total_recipients} recipients</p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Created</label>
                            <p className="mt-1 text-sm text-gray-900">{formatDate(viewingCampaign.created_at)}</p>
                          </div>
                          {viewingCampaign.sent_at && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Sent</label>
                              <p className="mt-1 text-sm text-gray-900">{formatDate(viewingCampaign.sent_at)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Campaign Analytics Modal */}
                {selectedCampaign && campaignAnalytics && (
                  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 my-8">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Campaign Analytics: {campaignAnalytics.campaign_name}</h3>
                        <button
                          onClick={() => {
                            setSelectedCampaign(null);
                            setCampaignAnalytics(null);
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          ✕
                        </button>
                      </div>
                      
                      {/* Performance Metrics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-gray-50 p-4 rounded">
                          <div className="text-2xl font-bold text-blue-600">{campaignAnalytics.total_recipients}</div>
                          <div className="text-sm text-gray-600">Total Recipients</div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded">
                          <div className="text-2xl font-bold text-green-600">{(campaignAnalytics.delivery_rate || 0).toFixed(1)}%</div>
                          <div className="text-sm text-gray-600">Delivery Rate</div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded">
                          <div className="text-2xl font-bold text-purple-600">{(campaignAnalytics.open_rate || 0).toFixed(1)}%</div>
                          <div className="text-sm text-gray-600">Open Rate</div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded">
                          <div className="text-2xl font-bold text-orange-600">{(campaignAnalytics.click_rate || 0).toFixed(1)}%</div>
                          <div className="text-sm text-gray-600">Click Rate</div>
                        </div>
                      </div>
                      
                      {/* Detailed Stats */}
                      <div className="mb-6 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div className="bg-blue-50 p-3 rounded">
                          <div className="font-semibold text-blue-900">Sent</div>
                          <div className="text-2xl font-bold text-blue-600">{campaignAnalytics.emails_sent}</div>
                        </div>
                        <div className="bg-green-50 p-3 rounded">
                          <div className="font-semibold text-green-900">Delivered</div>
                          <div className="text-2xl font-bold text-green-600">{campaignAnalytics.emails_delivered}</div>
                        </div>
                        <div className="bg-purple-50 p-3 rounded">
                          <div className="font-semibold text-purple-900">Opened</div>
                          <div className="text-2xl font-bold text-purple-600">{campaignAnalytics.emails_opened}</div>
                        </div>
                        <div className="bg-wayja-teal/10 p-3 rounded">
                          <div className="font-semibold text-orange-900">Clicked</div>
                          <div className="text-2xl font-bold text-orange-600">{campaignAnalytics.emails_clicked}</div>
                        </div>
                        <div className="bg-red-50 p-3 rounded">
                          <div className="font-semibold text-red-900">Bounced</div>
                          <div className="text-2xl font-bold text-red-600">{campaignAnalytics.emails_bounced}</div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded">
                          <div className="font-semibold text-gray-900">Failed</div>
                          <div className="text-2xl font-bold text-gray-600">{campaignAnalytics.emails_failed}</div>
                        </div>
                      </div>

                      {/* Undelivered Addresses */}
                      {campaignAnalytics.undelivered_addresses && campaignAnalytics.undelivered_addresses.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-semibold text-gray-900 mb-2">
                            Undelivered Addresses ({campaignAnalytics.undelivered_addresses.length})
                          </h4>
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                            <div className="space-y-2">
                              {campaignAnalytics.undelivered_addresses.map((addr, idx) => (
                                <div key={idx} className="text-sm border-b border-red-100 pb-2 last:border-b-0">
                                  <div className="font-medium text-red-900">{addr.email}</div>
                                  <div className="text-xs text-red-700 mt-1">
                                    Status: <span className="font-semibold">{addr.status}</span>
                                    {addr.error_message && (
                                      <> • Error: {addr.error_message}</>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Opened By */}
                      {campaignAnalytics.opened_by && campaignAnalytics.opened_by.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-semibold text-gray-900 mb-2">
                            Opened By ({campaignAnalytics.opened_by.length})
                          </h4>
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                            <div className="space-y-2">
                              {campaignAnalytics.opened_by.map((opener, idx) => (
                                <div key={idx} className="text-sm border-b border-green-100 pb-2 last:border-b-0">
                                  <div className="font-medium text-green-900">{opener.user_name}</div>
                                  <div className="text-xs text-green-700 mt-1">
                                    {opener.email} • Opened: {formatDate(opener.opened_at)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Show message if no data yet */}
                      {(!campaignAnalytics.undelivered_addresses || campaignAnalytics.undelivered_addresses.length === 0) &&
                       (!campaignAnalytics.opened_by || campaignAnalytics.opened_by.length === 0) && (
                        <div className="text-center text-gray-500 py-8">
                          <p>No detailed recipient data available yet.</p>
                          <p className="text-sm mt-2">Delivery and open tracking data will appear here once emails are processed.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Recipient Log Modal */}
                {viewingRecipients && (
                  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg max-w-6xl w-full mx-4 my-8 max-h-[90vh] flex flex-col">
                      {/* Sticky Header Section */}
                      <div className="flex-shrink-0 p-6 pb-4 border-b border-gray-200 bg-white rounded-t-lg relative z-20">
                        <div className="flex justify-between items-center mb-4">
                        <div>
                          <h3 className="text-lg font-semibold">Recipient Log</h3>
                          <p className="text-sm text-gray-600">
                            {campaigns.find(c => c.id === viewingRecipients)?.name}
                            {' • '}
                            {recipients.length} total recipients
                            {recipientStatusFilter !== 'all' || recipientSearchTerm ? (
                              <span className="text-gray-400">
                                {' • '}
                                {recipients.filter(r => {
                                  if (recipientStatusFilter !== 'all' && r.status !== recipientStatusFilter) return false;
                                  if (recipientSearchTerm) {
                                    const search = recipientSearchTerm.toLowerCase();
                                    return r.email_address.toLowerCase().includes(search) ||
                                           (r.user_name && r.user_name.toLowerCase().includes(search));
                                  }
                                  return true;
                                }).length} filtered
                              </span>
                            ) : null}
                          </p>
              </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => viewingRecipients && loadCampaignRecipients(viewingRecipients)}
                            disabled={loading}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                            title="Refresh recipient data"
                          >
                            Refresh
                          </button>
                          <button
                            onClick={() => {
                              setViewingRecipients(null);
                              setRecipients([]);
                              setRecipientStatusFilter('all');
                              setRecipientSearchTerm('');
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            ✕
                          </button>
                        </div>
                      </div>

                      {/* Filters and Search */}
                      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Search by Email
                          </label>
                          <input
                            type="text"
                            placeholder="Search email addresses..."
                            value={recipientSearchTerm}
                            onChange={(e) => setRecipientSearchTerm(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filter by Status
                          </label>
                          <select
                            value={recipientStatusFilter}
                            onChange={(e) => setRecipientStatusFilter(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                          >
                            <option value="all">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="queued">Queued</option>
                            <option value="sent">Sent</option>
                            <option value="delivered">Delivered</option>
                            <option value="opened">Opened</option>
                            <option value="clicked">Clicked</option>
                            <option value="bounced">Bounced</option>
                            <option value="failed">Failed</option>
                            <option value="unsubscribed">Unsubscribed</option>
                          </select>
                        </div>
                      </div>

                      {/* Status Summary - consistent with Analytics view */}
                      {(() => {
                        // Calculate stats consistently across all views
                        const pending = recipients.filter(r => ['pending', 'queued', 'queue_failed'].includes(r.status)).length;
                        const sent = recipients.filter(r => r.status === 'sent').length;
                        const delivered = recipients.filter(r => ['delivered', 'opened', 'clicked'].includes(r.status)).length; // Total delivered (includes opened/clicked)
                        const opened = recipients.filter(r => r.opened_at).length; // Based on timestamp, not status
                        const clicked = recipients.filter(r => r.clicked_at).length; // Based on timestamp
                        const unsubscribed = recipients.filter(r => r.status === 'unsubscribed').length;
                        const failed = recipients.filter(r => ['failed', 'bounced'].includes(r.status)).length;
                        
                        // Calculate percentages
                        const openRate = delivered > 0 ? ((opened / delivered) * 100).toFixed(1) : '0';
                        const clickRate = delivered > 0 ? ((clicked / delivered) * 100).toFixed(1) : '0';
                        
                        return (
                          <div className="mb-4 grid grid-cols-4 md:grid-cols-7 gap-2 text-sm">
                            <div className="bg-blue-50 p-2 rounded">
                              <div className="font-semibold text-blue-900 text-xs">Pending/Queued</div>
                              <div className="text-blue-600">{pending}</div>
                            </div>
                            <div className="bg-yellow-50 p-2 rounded">
                              <div className="font-semibold text-yellow-900 text-xs">Sent</div>
                              <div className="text-yellow-600 text-xs">awaiting delivery</div>
                              <div className="text-yellow-600">{sent}</div>
                            </div>
                            <div className="bg-green-50 p-2 rounded">
                              <div className="font-semibold text-green-900 text-xs">Delivered</div>
                              <div className="text-green-600 text-xs">reached inbox</div>
                              <div className="text-green-600">{delivered}</div>
                            </div>
                            <div className="bg-purple-50 p-2 rounded">
                              <div className="font-semibold text-purple-900 text-xs">Opened</div>
                              <div className="text-purple-600 text-xs">{openRate}%</div>
                              <div className="text-purple-600">{opened}</div>
                            </div>
                            <div className="bg-orange-50 p-2 rounded">
                              <div className="font-semibold text-orange-900 text-xs">Clicked</div>
                              <div className="text-orange-600 text-xs">{clickRate}%</div>
                              <div className="text-orange-600">{clicked}</div>
                            </div>
                            <div className="bg-gray-100 p-2 rounded">
                              <div className="font-semibold text-gray-700 text-xs">Unsubscribed</div>
                              <div className="text-gray-600">{unsubscribed}</div>
                            </div>
                            <div className="bg-red-50 p-2 rounded">
                              <div className="font-semibold text-red-900 text-xs">Failed/Bounced</div>
                              <div className="text-red-600">{failed}</div>
                            </div>
                          </div>
                        );
                      })()}
                      </div>

                      {/* Scrollable Table Section */}
                      <div className="flex-1 overflow-auto px-6 pt-0">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                Recipient
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                Timeline
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                Details
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {recipients
                              .filter(recipient => {
                                // Status filter
                                if (recipientStatusFilter !== 'all' && recipient.status !== recipientStatusFilter) {
                                  return false;
                                }
                                // Search filter
                                if (recipientSearchTerm) {
                                  const search = recipientSearchTerm.toLowerCase();
                                  return recipient.email_address.toLowerCase().includes(search) ||
                                         (recipient.user_name && recipient.user_name.toLowerCase().includes(search));
                                }
                                return true;
                              })
                              .map((recipient) => (
                                <tr key={recipient.id} className="hover:bg-gray-50">
                                  {/* Recipient Column */}
                                  <td className="px-4 py-3">
                                    <div className="text-sm font-medium text-gray-900">{recipient.email_address}</div>
                                    {recipient.user_name && (
                                      <div className="text-xs text-gray-500">{recipient.user_name}</div>
                                    )}
                                  </td>
                                  {/* Status Column */}
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    {getRecipientStatusBadge(recipient.status)}
                                  </td>
                                  {/* Timeline Column */}
                                  <td className="px-4 py-3 text-xs text-gray-500">
                                    <div className="space-y-1">
                                      {recipient.sent_at && (
                                        <div className="flex items-center gap-1">
                                          <span className="text-yellow-500">●</span>
                                          <span>Sent: {formatDate(recipient.sent_at)}</span>
                                        </div>
                                      )}
                                      {recipient.delivered_at && (
                                        <div className="flex items-center gap-1">
                                          <span className="text-green-500">●</span>
                                          <span>Delivered: {formatDate(recipient.delivered_at)}</span>
                                        </div>
                                      )}
                                      {recipient.opened_at && (
                                        <div className="flex items-center gap-1">
                                          <span className="text-purple-500">●</span>
                                          <span>Opened: {formatDate(recipient.opened_at)}</span>
                                        </div>
                                      )}
                                      {recipient.clicked_at && (
                                        <div className="flex items-center gap-1">
                                          <span className="text-blue-500">●</span>
                                          <span>Clicked: {formatDate(recipient.clicked_at)}</span>
                                        </div>
                                      )}
                                      {!recipient.sent_at && !recipient.delivered_at && !recipient.opened_at && !recipient.clicked_at && (
                                        <span className="text-gray-400">No activity yet</span>
                                      )}
                                    </div>
                                  </td>
                                  {/* Details Column */}
                                  <td className="px-4 py-3 text-xs">
                                    {recipient.error_message ? (
                                      <div className="text-red-600 max-w-xs" title={recipient.error_message}>
                                        <span className="font-medium">Error:</span> {recipient.error_message.length > 50 ? recipient.error_message.substring(0, 50) + '...' : recipient.error_message}
                                      </div>
                                    ) : recipient.sendgrid_message_id ? (
                                      <div className="text-gray-500 font-mono" title={recipient.sendgrid_message_id}>
                                        ID: {recipient.sendgrid_message_id.substring(0, 16)}...
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                        {recipients.filter(r => {
                          if (recipientStatusFilter !== 'all' && r.status !== recipientStatusFilter) return false;
                          if (recipientSearchTerm) {
                            const search = recipientSearchTerm.toLowerCase();
                            return r.email_address.toLowerCase().includes(search) ||
                                   (r.user_name && r.user_name.toLowerCase().includes(search));
                          }
                          return true;
                        }).length === 0 && (
                          <div className="p-8 text-center text-gray-500">
                            No recipients found matching your filters.
                          </div>
                        )}
                      </div>

                      {/* Footer Section */}
                      <div className="flex-shrink-0 p-6 pt-4 border-t border-gray-200 bg-white rounded-b-lg flex justify-end">
                        <button
                          onClick={() => {
                            const filtered = recipients.filter(r => {
                              if (recipientStatusFilter !== 'all' && r.status !== recipientStatusFilter) return false;
                              if (recipientSearchTerm) {
                                const search = recipientSearchTerm.toLowerCase();
                                return r.email_address.toLowerCase().includes(search) ||
                                       (r.user_name && r.user_name.toLowerCase().includes(search));
                              }
                              return true;
                            });
                            
                            const csv = [
                              ['Email', 'Name', 'Status', 'Sent At', 'Delivered At', 'Opened At', 'Clicked At', 'Message ID', 'Error'],
                              ...filtered.map(r => [
                                r.email_address,
                                r.user_name || '',
                                r.status,
                                r.sent_at || '',
                                r.delivered_at || '',
                                r.opened_at || '',
                                r.clicked_at || '',
                                r.sendgrid_message_id || '',
                                r.error_message || ''
                              ])
                            ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
                            
                            const blob = new Blob([csv], { type: 'text/csv' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `campaign-recipients-${viewingRecipients}-${new Date().toISOString()}.csv`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Export to CSV
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* User Lists Tab */}
            {activeTab === 'lists' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold">User Lists</h2>
                  <button
                    onClick={loadUserLists}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  >
                    Refresh
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {userLists.map((list) => (
                    <div key={list.id} className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-900">{list.name}</h3>
                      {list.description && (
                        <p className="text-sm text-gray-600 mt-1">{list.description}</p>
                      )}
                      <div className="mt-2 text-sm text-gray-500">
                        {list.member_count} members
                      </div>
                      <div className="mt-2 text-xs text-gray-400">
                        Created: {formatDate(list.created_at)}
                      </div>
                      <div className="mt-3 flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedListId(list.id);
                            setActiveTab('manage-list');
                            loadAvailableUsers();
                            loadListMembers(list.id);
                          }}
                          className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                        >
                          Manage Members
                        </button>
                        <button
                          onClick={() => deleteUserList(list.id, list.name)}
                          disabled={loading}
                          className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Create Campaign Tab */}
            {activeTab === 'create-campaign' && (
              <div className="max-w-2xl">
                <h2 className="text-lg font-semibold mb-6">Create New Campaign</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Campaign Name *</label>
                    <input
                      type="text"
                      value={newCampaign.name}
                      onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="e.g., Weekly Newsletter"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email Subject *</label>
                    <input
                      type="text"
                      value={newCampaign.subject}
                      onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="e.g., Your Weekly Market Update"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Message Template *</label>
                    <div className="mt-1">
                      <ReactQuill
                        value={newCampaign.message_template}
                        onChange={(value) => setNewCampaign({ ...newCampaign, message_template: value })}
                        placeholder="Hi {{first_name}},

Here's your weekly update...

Best regards,
The Wayja Team

Visit our website: [Wayja](https://wayja.me)"
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                            [{ 'color': [] }, { 'background': [] }],
                            [{ 'align': [] }],
                            ['link'],
                            ['clean']
                          ]
                        }}
                        formats={[
                          'header',
                          'bold', 'italic', 'underline', 'strike',
                          'list', 'bullet',
                          'color', 'background',
                          'align',
                          'link'
                        ]}
                        theme="snow"
                        style={{ minHeight: '200px' }}
                      />
                    </div>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-500">
                        <strong>Personalization:</strong> Use {`{{first_name}}`}, {`{{last_name}}`}, {`{{email}}`}, {`{{full_name}}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        <strong>Formatting:</strong> Use the toolbar above to format your text with bold, italics, underline, bullet points, and more
                      </p>
                      <p className="text-xs text-gray-500">
                        <strong>Links:</strong> Use the link button in the toolbar or type [Link Text](https://example.com) format
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sender Name</label>
                    <input
                      type="text"
                      value={newCampaign.sender_name}
                      readOnly
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-600 cursor-not-allowed"
                      title="Sender name is fixed for SendGrid authentication"
                    />
                    <p className="mt-1 text-xs text-gray-500">Fixed for SendGrid authentication</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sender Email</label>
                    <input
                      type="email"
                      value={newCampaign.sender_email}
                      readOnly
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-600 cursor-not-allowed"
                      title="Sender email is fixed for SendGrid authentication"
                    />
                    <p className="mt-1 text-xs text-gray-500">Fixed for SendGrid authentication</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Banner (Optional)
                    </label>
                    {newCampaign.banner_image_url ? (
                      <div className="space-y-2">
                        <div className="relative border border-gray-300 rounded-md overflow-hidden">
                          <img 
                            src={newCampaign.banner_image_url} 
                            alt="Email banner preview" 
                            className="w-full h-auto max-h-48 object-cover"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={removeBanner}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove Banner
                        </button>
                      </div>
                    ) : (
                      <div>
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <svg className="w-8 h-8 mb-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <p className="mb-2 text-sm text-gray-500">
                              <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 2MB</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleBannerUpload}
                            disabled={uploadingBanner}
                          />
                        </label>
                        {uploadingBanner && (
                          <p className="mt-2 text-sm text-blue-600">Uploading banner...</p>
                        )}
                      </div>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Upload a custom banner image for your email. Recommended size: 600x200px. If not provided, the default Wayja header will be used.
                    </p>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Select User Lists</label>
                      <div className="space-x-2">
                        <button
                          type="button"
                          onClick={() => setSelectedLists(userLists.map(list => list.id))}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedLists([])}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
                      {userLists.map((list) => (
                        <label key={list.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedLists.includes(list.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedLists([...selectedLists, list.id]);
                              } else {
                                setSelectedLists(selectedLists.filter(id => id !== list.id));
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm">
                            {list.name} ({list.member_count} members)
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={createCampaign}
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Campaign'}
                  </button>
                </div>
              </div>
            )}

            {/* Create List Tab */}
            {activeTab === 'create-list' && (
              <div className="max-w-md">
                <h2 className="text-lg font-semibold mb-6">Create New User List</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">List Name *</label>
                    <input
                      type="text"
                      value={newList.name}
                      onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="e.g., Premium Users"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={newList.description}
                      onChange={(e) => setNewList({ ...newList, description: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Optional description..."
                    />
                  </div>

                  <button
                    onClick={createUserList}
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create List'}
                  </button>
                </div>
              </div>
            )}

            {/* Manage Lists Tab */}
            {activeTab === 'manage-list' && selectedListId && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-lg font-semibold">Manage List Members</h2>
                    <p className="text-gray-600">
                      {userLists.find(l => l.id === selectedListId)?.name}
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveTab('lists')}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    ← Back to Lists
                  </button>
                </div>

                {/* User Management Table */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold">Manage List Members</h3>
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-600">
                          Total: {availableUsers.length} users • Members: {allMemberIds.length}
                        </div>
                      <div className="space-x-2">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              setLoading(true);
                              console.log('Loading ALL users for select all...');
                              
                                // Load ALL users from database
                              let allLoadedUsers: User[] = [];
                              let from = 0;
                              const pageSize = 1000;
                              let hasMore = true;

                              while (hasMore) {
                                const { data, error } = await supabase.rpc('get_bulk_email_users', {
                                  p_limit: pageSize,
                                  p_offset: from
                                });

                                if (error) throw error;

                                if (data && data.length > 0) {
                                  // Transform data to User objects
                                  const transformedData = data.map((user: any) => ({
                                    id: user.id,
                                    email: user.email,
                                    user_metadata: user.user_metadata,
                                    created_at: user.created_at,
                                    last_sign_in_at: user.last_sign_in_at
                                  }));
                                  allLoadedUsers = [...allLoadedUsers, ...transformedData];
                                  hasMore = data.length === pageSize;
                                  from += pageSize;
                                } else {
                                  hasMore = false;
                                }

                                // Safety limit - allow up to 100,000 users
                                if (from > 100000) {
                                  console.warn('Reached safety limit of 100,000 users');
                                  break;
                                }
                              }

                              console.log(`Select All: Loaded ${allLoadedUsers.length} total users`);
                              // Apply current filters to the loaded users
                              const filteredUsers = applyFilters(allLoadedUsers);
                              setSelectedUsers(filteredUsers.map(u => u.id));
                            } catch (err: any) {
                              console.error('Error in Select All:', err);
                              setError(`Failed to select all users: ${err.message}`);
                            } finally {
                              setLoading(false);
                            }
                          }}
                          disabled={loading}
                            className="text-sm text-purple-600 hover:text-purple-800 disabled:opacity-50"
                          >
                            {loading ? 'Loading...' : 'Select ALL Users'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedUsers(allMemberIds)}
                            className="text-sm text-green-600 hover:text-green-800"
                          >
                            Select All Members
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedUsers([])}
                            className="text-sm text-gray-600 hover:text-gray-800"
                        >
                            Clear Selection
                        </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Filters */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Login Activity
                        </label>
                        <select
                          value={loginFilter}
                          onChange={(e) => setLoginFilter(e.target.value as any)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          <option value="all">All Users</option>
                          <option value="never">Never Logged In</option>
                          <option value="recent">Logged In (Last 7 days)</option>
                          <option value="inactive">Inactive (30+ days)</option>
                          <option value="custom">Custom Days</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Account Age
                        </label>
                        <select
                          value={accountAgeFilter}
                          onChange={(e) => setAccountAgeFilter(e.target.value as any)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          <option value="all">All Accounts</option>
                          <option value="new">New (Last 7 days)</option>
                          <option value="old">Established (30+ days)</option>
                          <option value="custom">Custom Days Old</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Custom Days
                        </label>
                        <input
                          type="number"
                          value={customDays}
                          onChange={(e) => setCustomDays(parseInt(e.target.value) || 30)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="30"
                          min="1"
                          max="365"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <input
                        type="text"
                        placeholder="Search users by email, name..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                    </div>

                  {/* Action Buttons */}
                  <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-gray-600">
                        Showing {applyFilters(availableUsers).length} users matching filters
                        {selectedUsers.length > 0 && (
                          <>
                            {' • '}
                            {selectedUsers.filter(id => !allMemberIds.includes(id)).length} available •
                            {selectedUsers.filter(id => allMemberIds.includes(id)).length} members selected
                          </>
                        )}
                      </div>
                      <div className="space-x-2">
                        <button
                          onClick={() => selectedListId && addUsersToList(selectedListId, selectedUsers.filter(id => !allMemberIds.includes(id)))}
                          disabled={loading || selectedUsers.filter(id => !allMemberIds.includes(id)).length === 0}
                          className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 disabled:bg-gray-400"
                        >
                          Add Selected ({selectedUsers.filter(id => !allMemberIds.includes(id)).length})
                        </button>
                        <button
                          onClick={() => selectedListId && removeUsersFromList(selectedListId, selectedUsers.filter(id => allMemberIds.includes(id)))}
                          disabled={loading || selectedUsers.filter(id => allMemberIds.includes(id)).length === 0}
                          className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 disabled:bg-gray-400"
                        >
                          Remove Selected ({selectedUsers.filter(id => allMemberIds.includes(id)).length})
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Users Table */}
                  <div className="max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              checked={applyFilters(availableUsers).filter(user => {
                                const searchTerm = userSearchTerm.toLowerCase();
                                const email = user.email.toLowerCase();
                                const firstName = user.user_metadata?.first_name?.toLowerCase() || '';
                                const lastName = user.user_metadata?.last_name?.toLowerCase() || '';
                                const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
                                const matchesSearch = email.includes(searchTerm) || firstName.includes(searchTerm) || lastName.includes(searchTerm) || fullName.includes(searchTerm);
                                return matchesSearch;
                              }).every(user => selectedUsers.includes(user.id))}
                              onChange={(e) => {
                                const filteredUsers = applyFilters(availableUsers).filter(user => {
                                  const searchTerm = userSearchTerm.toLowerCase();
                                  const email = user.email.toLowerCase();
                                  const firstName = user.user_metadata?.first_name?.toLowerCase() || '';
                                  const lastName = user.user_metadata?.last_name?.toLowerCase() || '';
                                  const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
                                  const matchesSearch = email.includes(searchTerm) || firstName.includes(searchTerm) || lastName.includes(searchTerm) || fullName.includes(searchTerm);
                                  return matchesSearch;
                                });

                                if (e.target.checked) {
                                  const newSelected = [...new Set([...selectedUsers, ...filteredUsers.map(u => u.id)])];
                                  setSelectedUsers(newSelected);
                                } else {
                                  const filteredIds = filteredUsers.map(u => u.id);
                                  setSelectedUsers(selectedUsers.filter(id => !filteredIds.includes(id)));
                                }
                              }}
                              className="rounded"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Login
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Account Age
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {applyFilters(availableUsers)
                        .filter(user => {
                            // Show user if they match the search term OR are currently selected
                          const searchTerm = userSearchTerm.toLowerCase();
                          const email = user.email.toLowerCase();
                          const firstName = user.user_metadata?.first_name?.toLowerCase() || '';
                          const lastName = user.user_metadata?.last_name?.toLowerCase() || '';
                          const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
                          
                            const matchesSearch = email.includes(searchTerm) ||
                             firstName.includes(searchTerm) || 
                             lastName.includes(searchTerm) || 
                                                 fullName.includes(searchTerm);

                            const isSelected = selectedUsers.includes(user.id);

                            return matchesSearch || isSelected;
                          })
                          .map((user) => {
                            const isMember = allMemberIds.includes(user.id);
                            return (
                              <tr key={user.id} className={`hover:bg-gray-50 ${isMember ? 'bg-green-50' : ''}`}>
                                <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUsers([...selectedUsers, user.id]);
                                } else {
                                  setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                }
                              }}
                                    className="rounded"
                            />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                                    <div className="text-sm font-medium text-gray-900">{user.email}</div>
                              {user.user_metadata?.first_name && (
                                      <div className="text-sm text-gray-500">
                                  {user.user_metadata.first_name} {user.user_metadata.last_name}
                                </div>
                              )}
                            </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {user.last_sign_in_at
                                    ? new Date(user.last_sign_in_at).toLocaleDateString()
                                    : 'Never'
                                  }
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {Math.floor((new Date().getTime() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24))} days
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    isMember
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {isMember ? 'Member' : 'Available'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                    {applyFilters(availableUsers).filter(user => {
                      const searchTerm = userSearchTerm.toLowerCase();
                      const email = user.email.toLowerCase();
                      const firstName = user.user_metadata?.first_name?.toLowerCase() || '';
                      const lastName = user.user_metadata?.last_name?.toLowerCase() || '';
                      const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
                      const matchesSearch = email.includes(searchTerm) || firstName.includes(searchTerm) || lastName.includes(searchTerm) || fullName.includes(searchTerm);
                      const isSelected = selectedUsers.includes(user.id);
                      return matchesSearch || isSelected;
                    }).length === 0 && (
                      <div className="p-8 text-center text-gray-500">
                        No users found matching your current filters and search.
                      </div>
                    )}
                              </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
